import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { io } from 'socket.io-client';
import { SOCKET_URL, API_BASE_URL } from '../config';
import Navigation from '../components/Navigation';
import LiveGamesSidebar from '../components/LiveGamesSidebar';
import { teams } from '../data/teams';
import { useStandings } from '../hooks/useStandings';

interface PlayerStats {
  id: string;
  name: string;
  mechClass: string;
  teamId: string;
  gamesPlayed: number;
  totalPoints: number;
  totalAssists: number;
  totalRebounds: number;
  avgPoints: number;
  avgAssists: number;
  avgRebounds: number;
  rating: number;
}

interface RostersData {
  [teamId: string]: PlayerStats[];
}

const RostersPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [rosters, setRosters] = useState<RostersData>({});
  const [loading, setLoading] = useState(true);
  const { getTeamStanding } = useStandings();
  const teamRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  useEffect(() => {
    fetchRosters();
    
    // Set up socket connection for live updates
    const newSocket = io(SOCKET_URL, {
      transports: ['websocket', 'polling']
    });

    newSocket.on('connect', () => {
      // Connected to rosters update server
    });

    newSocket.on('disconnect', () => {
      // Disconnected from rosters update server
    });

    // Refresh rosters when games complete
    newSocket.on('continuous-game-complete', () => {
      fetchRosters();
    });

    // Also refresh on game updates (for live stat tracking)
    newSocket.on('continuous-game-update', () => {
      // Debounce: only refresh every 5 seconds max
      const now = Date.now();
      if (!(window as any).lastRosterUpdate || now - (window as any).lastRosterUpdate > 5000) {
        (window as any).lastRosterUpdate = now;
        fetchRosters();
      }
    });

    return () => {
      newSocket.close();
    };
  }, []);

  // Scroll to team when team query parameter is present
  useEffect(() => {
    const teamId = searchParams.get('team');
    if (teamId && !loading && teamRefs.current[teamId]) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        const element = teamRefs.current[teamId];
        if (element) {
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - 150;
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [searchParams, loading]);

  const fetchRosters = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/rosters`);
      const data = await response.json();
      if (data.success) {
        setRosters(data.rosters || {});
        
        // Debug: Compare team IDs to identify mismatches
        const apiTeamIds = Object.keys(data.rosters || {}).sort();
        const localTeamIds = teams.map(t => t.id).sort();
        const inApiNotLocal = apiTeamIds.filter(id => !teams.find(t => t.id === id));
        const inLocalNotApi = teams.filter(t => !data.rosters || !data.rosters[t.id]).map(t => t.id);
        
        console.log('🔍 [ROSTERS DEBUG] API teams:', apiTeamIds.length, '| Local teams:', localTeamIds.length);
        console.log('🔍 [ROSTERS DEBUG] API team IDs:', apiTeamIds);
        console.log('🔍 [ROSTERS DEBUG] Local team IDs:', localTeamIds);
        if (inApiNotLocal.length > 0) {
          console.log('⚠️ [ROSTERS DEBUG] In API but not local:', inApiNotLocal);
        }
        if (inLocalNotApi.length > 0) {
          console.log('⚠️ [ROSTERS DEBUG] In local but not API:', inLocalNotApi);
        }
        if (inApiNotLocal.length === 0 && inLocalNotApi.length === 0) {
          console.log('✅ [ROSTERS DEBUG] All team IDs match!');
        }
      } else {
        console.error('API returned success: false', data);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching rosters:', error);
      setLoading(false);
    }
  };

  // Merge team data with standings
  const teamsWithStandings = teams.map(team => {
    const standing = getTeamStanding(team.id);
    return {
      ...team,
      wins: standing ? standing.wins : 0,
      losses: standing ? standing.losses : 0
    };
  });

  // Group teams by league
  const nationalTeams = teamsWithStandings.filter(team => team.league === 'National League');
  const corporateTeams = teamsWithStandings.filter(team => team.league === 'Corporate League');

  const renderTeamTable = (team: typeof teams[0]) => {
    // Always use rosters data which now includes all players from team roster
    let displayPlayers = rosters[team.id] || [];
    
    // Fallback: if no roster data, show players from team data directly
    if (displayPlayers.length === 0 && team.pilots) {
      console.log(`⚠️ [ROSTERS DEBUG] No API data for ${team.id} (${team.name}), using local team.pilots`);
      
      // Calculate weighted sums for all players in this team to find min/max
      const teamWeightedSums = team.pilots.map(pilot => {
        const focus = (pilot.focus || 10) * 2.0;
        const reaction = (pilot.reaction || 10) * 1.75;
        const tactics = (pilot.tactics || 10) * 2.0;
        const targeting = pilot.mech ? (pilot.mech.targeting || 10) * 2.0 : 10;
        const servoResponse = pilot.mech ? (pilot.mech.servoResponse || 10) * 1.75 : 10;
        const jumpJets = pilot.mech ? (pilot.mech.jumpJets || 10) * 1.5 : 10;
        const armor = pilot.mech ? (pilot.mech.armor || 10) * 1.25 : 10;
        const heatManagement = pilot.mech ? (pilot.mech.heatManagement || 10) * 0.75 : 10;
        return focus + reaction + tactics + targeting + servoResponse + jumpJets + armor + heatManagement;
      });
      
      const teamMinWeighted = teamWeightedSums.length > 0 ? Math.min(...teamWeightedSums) : 91;
      const teamMaxWeighted = teamWeightedSums.length > 0 ? Math.max(...teamWeightedSums) : 247;
      
      displayPlayers = team.pilots.map((pilot, idx) => {
        // Calculate weighted rating based on performance impact
        const focus = (pilot.focus || 10) * 2.0;
        const reaction = (pilot.reaction || 10) * 1.75;
        const tactics = (pilot.tactics || 10) * 2.0;
        
        const targeting = pilot.mech ? (pilot.mech.targeting || 10) * 2.0 : 10;
        const servoResponse = pilot.mech ? (pilot.mech.servoResponse || 10) * 1.75 : 10;
        const jumpJets = pilot.mech ? (pilot.mech.jumpJets || 10) * 1.5 : 10;
        const armor = pilot.mech ? (pilot.mech.armor || 10) * 1.25 : 10;
        const heatManagement = pilot.mech ? (pilot.mech.heatManagement || 10) * 0.75 : 10;
        
        const weightedSum = focus + reaction + tactics + targeting + servoResponse + jumpJets + armor + heatManagement;
        // Use actual min/max from team's players for normalization
        const normalized = Math.max(0, Math.min(1, (weightedSum - teamMinWeighted) / (teamMaxWeighted - teamMinWeighted)));
        // Apply moderate curve: power 0.5 provides balanced separation
        const curved = Math.pow(normalized, 0.5);
        const rating = Math.round(40 + curved * 56);
        
        return {
          id: pilot.id || `${team.id}-${idx}`,
          name: pilot.name,
          mechClass: pilot.mech?.class || 'UNKNOWN',
          teamId: team.id,
          gamesPlayed: 0,
          totalPoints: 0,
          totalAssists: 0,
          totalRebounds: 0,
          avgPoints: 0,
          avgAssists: 0,
          avgRebounds: 0,
          rating: Math.min(100, Math.max(60, rating))
        };
      }).sort((a, b) => b.rating - a.rating);
    }
    

    return (
      <div 
        key={team.id} 
        ref={(el) => { teamRefs.current[team.id] = el; }}
        style={{ marginBottom: '3rem', overflow: 'scroll' }}
      >
        <div className="table--team rosters">
          <div className="thead">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div 
                  className="team-dot"
                  style={{
                    backgroundColor: team.color || '#666',
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    flexShrink: 0
                  }}
                ></div>
                <div>{team.name}</div>
              </div>
              <div style={{ fontSize: '0.875rem', opacity: 0.7 }}>
                {team.wins}-{team.losses}
              </div>
            </div>
          </div>
          
          {/* Table Header */}
          <div className="trow" style={{ 
            display: 'flex', 
            gap: '0.75rem', 
            padding: '0.25rem 0',
            borderBottom: '1px solid rgba(255, 255, 255, 0.25)',
            fontSize: '0.75rem',
            opacity: 0.5,
            fontWeight: 'normal'
          }}>
            <div style={{ flex: '1.6', textAlign: 'left' }}>Name</div>
            <div style={{ flex: '1.5', textAlign: 'left' }}>Mech</div>
            <div style={{ flex: '0.8', textAlign: 'right' }}>Rating</div>
            <div style={{ flex: '0.8', textAlign: 'right' }}>PTS</div>
            <div style={{ flex: '0.8', textAlign: 'right' }}>AST</div>
            <div style={{ flex: '0.8', textAlign: 'right' }}>REB</div>
            <div style={{ flex: '0.8', textAlign: 'right' }}>AVG PTS</div>
            <div style={{ flex: '0.8', textAlign: 'right' }}>AVG AST</div>
            <div style={{ flex: '0.8', textAlign: 'right' }}>AVG REB</div>
          </div>

          {/* Player Rows */}
          {displayPlayers.map((player, index) => (
            <div key={player.id || `player-${index}`} className="trow player w-inline-block">
              <div className="player-name" style={{ flex: '1.5', textAlign: 'left' }}>{player.name}</div>
              <div className="player-mech" style={{ flex: '1.5', textAlign: 'left' }}>{player.mechClass}</div>
              <div style={{ flex: '0.58', textAlign: 'right', fontWeight: 'bold' }}>
                {player.rating}
              </div>
              <div style={{ flex: '0.8', textAlign: 'right' }}>
                {player.totalPoints !== undefined && player.totalPoints !== null ? player.totalPoints : '-'}
              </div>
              <div style={{ flex: '0.8', textAlign: 'right' }}>
                {player.totalAssists !== undefined && player.totalAssists !== null ? player.totalAssists : '-'}
              </div>
              <div style={{ flex: '0.8', textAlign: 'right' }}>
                {player.totalRebounds !== undefined && player.totalRebounds !== null ? player.totalRebounds : '-'}
              </div>
              <div style={{ flex: '0.8', textAlign: 'right' }}>
                {player.gamesPlayed > 0 && player.avgPoints !== undefined && player.avgPoints !== null ? player.avgPoints.toFixed(1) : '-'}
              </div>
              <div style={{ flex: '0.8', textAlign: 'right' }}>
                {player.gamesPlayed > 0 && player.avgAssists !== undefined && player.avgAssists !== null ? player.avgAssists.toFixed(1) : '-'}
              </div>
              <div style={{ flex: '0.8', textAlign: 'right' }}>
                {player.gamesPlayed > 0 && player.avgRebounds !== undefined && player.avgRebounds !== null ? player.avgRebounds.toFixed(1) : '-'}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <>
        <Navigation />
        <div className="container--interior">
          <LiveGamesSidebar />
          <div className="interior-wrapper">
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '16rem' }}>
              <div style={{ color: '#22d3ee' }}>
                <div style={{ animation: 'pulse 2s infinite' }}>Loading rosters...</div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navigation />
      
      <div className="container--interior">
        <LiveGamesSidebar />
        
        <div className="interior-wrapper">
          <div style={{ marginBottom: '-2.25rem', paddingTop: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h1 style={{ margin: 0, fontSize: '2rem' }}></h1>
            <Link 
              to="/league-leaders" 
              style={{
                padding: '0.5rem 1rem',
                border: '1px solid #fff',
                borderRadius: '4px',
                color: '#fff',
                textDecoration: 'none',
                fontSize: '0.875rem',
                transition: 'all 0.2s'
              }}
            >
              League Leaders →
            </Link>
          </div>
          
          {/* National League */}
          <div style={{ marginBottom: '4rem' }}>
            <h2 style={{ marginBottom: '1.5rem', fontSize: '1.5rem', paddingBottom: '0.5rem' }}>
              National League
            </h2>
            {nationalTeams.map(team => renderTeamTable(team))}
          </div>

          {/* Corporate League */}
          <div style={{ marginBottom: '4rem' }}>
            <h2 style={{ marginBottom: '1.5rem', fontSize: '1.5rem', paddingBottom: '0.5rem' }}>
              Corporate League
            </h2>
            {corporateTeams.map(team => renderTeamTable(team))}
          </div>
          
          <p className="copyright"><span className="copyright-icon">© </span>2077 Neo Vegas CORP Trust</p>
        </div>
      </div>
    </>
  );
};

export default RostersPage;

